import React, { memo } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { AutoAwesomeRounded, PanToolOutlined, TuneRounded } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import type { Problem } from '../../../services/timetableService';
import ProblemList from '../ProblemList';

interface BoardStartProps {
  sessionName: string;
  sessionId: string;
  problems: Problem[];
  canWrite: boolean;
  busy: boolean;
  onGenerate: () => void;
  onManual: () => void;
}

/** The first visit: what's missing, then generate or build by hand. */
const BoardStart = memo<BoardStartProps>(({ sessionName, sessionId, problems, canWrite, busy, onGenerate, onManual }) => {
  const errors = problems.filter((p) => p.severity === 'error');
  return (
    <Paper variant="outlined" sx={{ borderRadius: '16px', px: { xs: 3, md: 6 }, py: { xs: 4, md: 6 } }}>
      <Box sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700}>Build the {sessionName} timetable</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {canWrite
            ? 'Generate a complete timetable in about a minute, or place periods by hand. Either way you can drag anything around afterwards, then publish when it looks right.'
            : 'No timetable has been published for this session yet.'}
        </Typography>
        {canWrite && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
            {errors.length ? (
              <Button component={RouterLink} to="/app/timetable/setup" variant="contained" startIcon={<TuneRounded />} sx={{ textTransform: 'none' }}>
                Finish setup
              </Button>
            ) : (
              <Button variant="contained" startIcon={<AutoAwesomeRounded />} onClick={onGenerate} disabled={busy} sx={{ textTransform: 'none' }}>
                Generate timetable
              </Button>
            )}
            <Button variant="outlined" startIcon={<PanToolOutlined />} onClick={onManual} disabled={busy || !!errors.length} sx={{ textTransform: 'none' }}>
              Build by hand
            </Button>
          </Stack>
        )}
      </Box>
      {canWrite && problems.length > 0 && (
        <Box sx={{ maxWidth: 720, mx: 'auto', mt: 4 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
            {errors.length ? 'Before you start' : 'Worth a look'}
          </Typography>
          <ProblemList problems={problems} sessionId={sessionId} />
        </Box>
      )}
    </Paper>
  );
});

BoardStart.displayName = 'BoardStart';

export default BoardStart;
